/* eslint-disable react/no-unknown-property */
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import {
  Environment,
  OrbitControls,
  PerspectiveCamera,
  useContextBridge,
  useProgress,
} from '@react-three/drei';
import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import Style from './style.module.scss';
import Marker from 'componentsForThree/Marker';
import { ControlContext } from 'provider/ControlProvider';
import Popup from 'components/Popup';
import controlBtn from 'assets/img/back.png';
import logo from 'assets/img/reproduction.png';
import MarkerParent from 'componentsForThree/MarkerParent';
import { PuffLoader } from 'react-spinners';
import Model from 'componentsForThree/ModelLoader';

type MarkerInfo = {
  name: string;
  pos: Array<number>;
};
type CameraProps = {
  pos: Array<number>;
};

const zone = new Array<MarkerInfo>(
  {
    name: 'Uterus',
    pos: [-0.093, 2.966, -0.761],
  },
  {
    name: 'Cervix',
    pos: [-0.093, -2, -0.761],
  },
  {
    name: 'Vagina',
    pos: [-0.093, -3.928, -0.761],
  },
  {
    name: 'Ovaries',
    pos: [4.284, 0.797, 1.33],
  },
  {
    name: 'Ovaries',
    pos: [-4.284, 0.797, 0.6],
  },
  {
    name: 'Fallopian tubes',
    pos: [-6.485, 4.595, 2.984],
  },
  {
    name: 'Fallopian tubes',
    pos: [6.485, 4.595, 2.984],
  }
);

export default function Configurator() {
  const ContextBridge = useContextBridge(ControlContext);
  const { active, progress } = useProgress();
  const { buttonIndex, popupRef, setButtonIndex } = useContext(ControlContext);
  const [sindex, setIndex] = useState<number>(-1);
  const [markVisible, SetMarkVisible] = useState<boolean>(false);
  const orbit = useRef<any>();
  const camera = useRef<any>();
  const myMesh = useRef<THREE.Mesh>();
  const [zoneIndex, setZoneIndex] = useState<number>(-1);
  const [drag, setDrag] = useState<boolean>(false);
  useEffect(() => {
    if (progress === 100 && !active) {
      if (drag)
        orbit.current.touches = {
          ONE: THREE.TOUCH.PAN,
          TWO: THREE.TOUCH.DOLLY_PAN,
        };
      else
        orbit.current.touches = {
          ONE: THREE.TOUCH.ROTATE,
          TWO: THREE.TOUCH.DOLLY_PAN,
        };
    }
  }, [drag]);
  const cameraMove = ({ pos }: CameraProps) => {
    gsap.to(orbit.current.target, {
      duration: 1,
      ease: 'back.out(0.8)',
      x: pos[0],
      y: pos[1],
      z: pos[2],
    });
    gsap.to(orbit.current, {
      onStart: () => {
        orbit.current.minDistance = 7;
      },
      duration: 1.2,
      ease: 'power1.out',
      maxDistance: 40,
    });
    gsap.to('#resetBtn', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
  };
  const cameraRest = () => {
    setIndex(-1);
    gsap.to(orbit.current.target, {
      duration: 1,
      ease: 'back.out(0.8)',
      x: 0,
      y: 0,
      z: 0,
    });

    gsap.to(orbit.current, {
      onStart: () => {
        orbit.current.maxDistance = 230;
      },
      duration: 1.2,
      ease: 'power1.out',
      minDistance: 60,
    });
    setZoneIndex(-1);
    canvaseMoveRight();
    popupInitAnim();
  };
  const canvaseMoveLeft = () => {
    if (window.innerWidth >= 1024) {
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -popupRef.current.offsetWidth + 200,
      });
      gsap.to('#resetBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -400,
      });
      gsap.to('#controlBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: -200,
      });
    } else
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        y: -200,
        onComplete: () => {
          gsap.to('#controlBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 200,
          });
          gsap.to('#resetBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: -400,
          });
        },
      });
  };
  const canvaseMoveRight = () => {
    if (window.innerWidth >= 1024) {
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
      gsap.to('#resetBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
      gsap.to('#controlBtn', {
        duration: 1,
        ease: 'back.out(0.8)',
        x: 0,
      });
    } else
      gsap.to('#canvas', {
        duration: 1,
        ease: 'back.out(0.8)',
        y: 0,
        onComplete: () => {
          gsap.to('#controlBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 0,
          });
          gsap.to('#resetBtn', {
            duration: 1,
            ease: 'back.out(0.8)',
            y: 0,
          });
        },
      });
  };
  const markAnim = () => {
    gsap.to('#marker', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 0,
    });
    gsap.to('#plusMark', {
      duration: 0.3,
      ease: 'power4.Out',
      fontSize: 1,
    });
  };
  const markInitAnim = () => {
    gsap.to('#marker', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
    gsap.to('#plusMark', {
      duration: 0.3,
      ease: 'power4.Out',
      fontSize: 20,
    });
  };
  const popupAnim = (index: number) => {
    gsap.to('#popup', {
      onStart: () => {
        setButtonIndex(index);
      },
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
      onComplete: () => {
        canvaseMoveLeft();
      },
    });
    gsap.to('#resetBtn', {
      duration: 1,
      ease: 'power4.Out',
      opacity: 1,
    });
    gsap.fromTo(
      '#textInfo',
      { duration: 1, ease: 'power1.out', opacity: 0 },
      { duration: 1, ease: 'power1.out', opacity: 1 }
    );
  };
  const popupInitAnim = () => {
    gsap.to('#popup', {
      duration: 1,
      ease: 'power1.out',
      opacity: 0,
      onComplete: () => {
        setButtonIndex(-1);
      },
    });
    gsap.to('#resetBtn', {
      duration: 0.5,
      ease: 'power4.Out',
      opacity: 0,
    });
  };
  return (
    <div
      className={Style.wrapper}
      onPointerUp={() => {
        if (markVisible) {
          SetMarkVisible(false);
          markInitAnim();
          if (drag) setDrag(false);
        }
      }}
    >
      <div id='canvas' className='relative w-screen h-screen'>
        <Suspense fallback={<Loader />}>
          <Canvas
            dpr={[1, 2]}
            onPointerDown={() => {
              if (!markVisible) {
                SetMarkVisible(true);
                markAnim();
              }
            }}
          >
            {/* <Environment files={'model/env.hdr'} /> */}
            <ambientLight intensity={0.2} />
            <pointLight intensity={1} position={[10, 0, 4]} />
            <pointLight intensity={1} position={[0, 8, -10]} />
            <ContextBridge>
              <Model />
              {zone.map((com, index) => (
                <Marker
                  key={index}
                  name={com.name}
                  position={com.pos}
                  status={true}
                  oclusionObj={myMesh as any}
                  colorIndex={0}
                  openStatu={sindex === index ? true : false}
                  setButtonAction={(e) => {
                    cameraMove({ pos: com.pos });
                    setIndex(index);
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    popupAnim(index);
                  }}
                />
              ))}
              <mesh
                scale={[4, 2, 10]}
                position={[-0.2, 2, -2]}
                rotation={[-Math.PI / 3, 0, 0]}
                onPointerDown={() => {
                  if (!drag) setDrag(true);
                }}
                visible={false}
              >
                <boxGeometry />
                <meshPhongMaterial color='royalblue' opacity={0.2} />
              </mesh>
              <mesh
                ref={myMesh as any}
                scale={[100, 100, 0.2]}
                rotation={[0, 0, 0]}
                position={[0, 5, -1.7]}
                visible={false}
              >
                <boxGeometry />
                <meshPhongMaterial color='royalblue' />
              </mesh>
              <PerspectiveCamera
                makeDefault
                ref={camera}
                position={[0, 0, 60]}
                zoom={window.innerWidth > 640 ? 2 : 1}
                fov={40}
              />
              <OrbitControls
                ref={orbit}
                target={[0, 0, 0]}
                enableRotate={!drag}
                minDistance={7}
                maxDistance={200}
                minPolarAngle={0}
                maxPolarAngle={Math.PI / 1.75}
                touches={{
                  ONE: THREE.TOUCH.ROTATE,
                  TWO: THREE.TOUCH.DOLLY_PAN,
                }}
              />
            </ContextBridge>
          </Canvas>
        </Suspense>
        <img
          id={'controlBtn'}
          className='absolute top-6 right-6 md:top-16 md:right-16 w-16 h-16 md:w-20 md:h-20 select-none'
          src={logo}
        />
      </div>
      <button
        id='resetBtn'
        className='absolute bottom-6 right-6 md:bottom-20 md:right-16 w-10 h-10 rounded-full drop-shadow-sm shadow-md bg-white opacity-0 z-50'
        onClick={cameraRest}
      >
        <img className='p-2' src={controlBtn} />
      </button>
      <Popup />
    </div>
  );
}

function Loader() {
  return (
    <div className='text-2xl font-OS_SB p-12 text-gray-500 h-screen w-screen flex justify-center items-center text-center'>
      <div className='relative'>
        <PuffLoader color='#36d7b7' size={300} />
        <p className='absolute left-24 top-36'>Loading...</p>
      </div>
    </div>
  );
}
